import type { FailReason } from '../../lib/api';
import type { Vec2 } from '../../render/homography';

export const MAX_OUTLINE_POINTS = 8;

/** Manual fallback: tap the floor corners (3 to 8 points) to build the floor polygon. */
export function OutlineOverlay({ points, onAdd }: { points: Vec2[]; onAdd(p: Vec2): void }) {
  return (
    <div
      data-testid="outline-editor"
      class="absolute inset-0 cursor-crosshair"
      style={{ touchAction: 'none' }}
      onPointerDown={(e) => {
        if (points.length >= MAX_OUTLINE_POINTS) return;
        const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
        onAdd([(e.clientX - r.left) / r.width, (e.clientY - r.top) / r.height]);
      }}
    >
      <svg class="pointer-events-none absolute inset-0 h-full w-full" viewBox="0 0 1 1" preserveAspectRatio="none" aria-hidden="true">
        {points.length > 1 && (
          <polygon
            points={points.map((p) => p.join(',')).join(' ')}
            fill={points.length > 2 ? 'rgba(255,255,255,0.25)' : 'none'}
            stroke="white"
            stroke-width="2.5"
            vector-effect="non-scaling-stroke"
          />
        )}
      </svg>
      {points.map((p, i) => (
        <span
          key={i}
          class="pointer-events-none absolute grid h-6 w-6 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full border-2 border-white bg-brand text-[11px] font-bold text-brand-text shadow"
          style={{ left: `${p[0] * 100}%`, top: `${p[1] * 100}%` }}
        >
          {i + 1}
        </span>
      ))}
    </div>
  );
}

interface ToolbarProps {
  /** Why we got here; sets the message. */
  reason: FailReason;
  /** Offered for connection problems: detect the floor again. */
  onRetry?(): void;
  points: Vec2[];
  onUndo(): void;
  onClear(): void;
  onDone(): void;
}

const MESSAGES: Record<FailReason, { title: string; text: string }> = {
  connection: {
    title: 'Connection problem',
    text: "We couldn't reach our server to find your floor. Check your internet connection and tap Try again, or tap each corner of the floor in the photo, going around the edge",
  },
  rate_limited: {
    title: 'Outline your floor',
    text: "You've tried a lot of photos in the last hour, so automatic floor finding is paused for a bit. Tap each corner of the floor in the photo, going around the edge",
  },
  detection: {
    title: 'Outline your floor',
    text: "We couldn't find the floor on our own. Tap each corner of the floor in the photo, going around the edge",
  },
};

export function OutlineToolbar({ reason, onRetry, points, onUndo, onClear, onDone }: ToolbarProps) {
  const { title, text } = MESSAGES[reason];
  return (
    <div class="space-y-3 px-4 pt-3 sm:px-0">
      <div>
        <h2 class="text-base font-bold">{title}</h2>
        <p class="text-sm text-muted">
          {text} ({points.length} of up to {MAX_OUTLINE_POINTS} points).
        </p>
      </div>
      {reason === 'connection' && onRetry && (
        <button type="button" onClick={onRetry} class="min-h-11 w-full rounded-full bg-brand px-5 text-sm font-semibold text-brand-text hover:brightness-110">
          Try again
        </button>
      )}
      <div class="flex flex-wrap items-center gap-2">
        <button type="button" disabled={!points.length} onClick={onUndo} class="min-h-11 rounded-full px-4 text-sm font-medium ring-1 ring-line hover:bg-paper disabled:opacity-40">
          Undo point
        </button>
        <button type="button" disabled={!points.length} onClick={onClear} class="min-h-11 rounded-full px-4 text-sm font-medium ring-1 ring-line hover:bg-paper disabled:opacity-40">
          Clear
        </button>
        <span class="flex-1" />
        <button type="button" disabled={points.length < 3} onClick={onDone} class="min-h-11 rounded-full bg-ink px-5 text-sm font-semibold text-white disabled:opacity-40">
          Done
        </button>
      </div>
    </div>
  );
}
